import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const TranscriptionContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TranscriptionProvider = ({ children }) => {
  // 'local' = free tier (browser Whisper), 'cloud' = premium (Groq Whisper)
  const [mode, setMode] = useState('cloud');
  const [tier, setTier] = useState('free');
  const [localModelReady, setLocalModelReady] = useState(false);
  const [localModelLoading, setLocalModelLoading] = useState(false);
  const [localModelProgress, setLocalModelProgress] = useState('');
  const [localModelFailed, setLocalModelFailed] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);

  const workerRef = useRef(null);
  const pendingResolveRef = useRef(null);

  // Fetch user tier on mount
  useEffect(() => {
    fetchTier();
  }, []);

  const fetchTier = async () => {
    try {
      const res = await axios.get(`${API}/ai/hybrid/tier`);
      const userTier = res.data.tier;
      setTier(userTier);
      // Default: always use cloud for reliability; local is opt-in
      setMode('cloud');
    } catch {
      setTier('free');
      setMode('cloud');
    }
  };

  // Initialize the Web Worker for local Whisper
  const initLocalWhisper = useCallback(() => {
    if (workerRef.current || localModelLoading || localModelFailed) return;

    setLocalModelLoading(true);
    setLocalModelProgress('Initializing...');

    try {
      const worker = new Worker('/whisper-worker.js');
      workerRef.current = worker;

      worker.onmessage = (e) => {
        const { type, message, transcript, chunks } = e.data;

        switch (type) {
          case 'status':
            setLocalModelProgress(message);
            break;
          case 'ready':
            setLocalModelReady(true);
            setLocalModelLoading(false);
            setLocalModelProgress('');
            toast.success('Local Whisper model loaded!');
            break;
          case 'result':
            setIsTranscribing(false);
            if (pendingResolveRef.current) {
              pendingResolveRef.current({
                success: true,
                transcript,
                chunks,
                model: 'local/whisper-tiny',
                tier: 'free',
              });
              pendingResolveRef.current = null;
            }
            break;
          case 'error':
            setIsTranscribing(false);
            setLocalModelLoading(false);
            setLocalModelFailed(true);
            setLocalModelProgress('');
            // Don't show scary errors — provide actionable info
            toast.info(message || 'Local model unavailable. Use Cloud transcription instead.');
            if (pendingResolveRef.current) {
              pendingResolveRef.current({ success: false, error: message });
              pendingResolveRef.current = null;
            }
            // Auto-switch to cloud
            setMode('cloud');
            break;
          default:
            break;
        }
      };

      // Handle Worker-level errors (e.g., script load failures)
      worker.onerror = (event) => {
        // Prevent the error from propagating as an uncaught exception
        event.preventDefault();
        console.warn('Whisper worker error (handled):', event.message);
        setLocalModelLoading(false);
        setLocalModelFailed(true);
        setLocalModelProgress('');
        // Terminate broken worker
        worker.terminate();
        workerRef.current = null;
        // Auto-switch to cloud
        setMode('cloud');
        toast.info('Local transcription unavailable. Using cloud mode.');
      };

      worker.postMessage({ type: 'load' });
    } catch (err) {
      console.warn('Failed to create Whisper worker:', err);
      setLocalModelLoading(false);
      setLocalModelFailed(true);
      setMode('cloud');
    }
  }, [localModelLoading, localModelFailed]);

  // Transcribe audio — routes to local or cloud based on mode
  const transcribe = useCallback(async (audioBlob) => {
    setIsTranscribing(true);

    if (mode === 'cloud') {
      // Cloud: send to Groq via backend (works for all tiers)
      try {
        const formData = new FormData();
        const ext = audioBlob.type.includes('webm') ? 'webm' : 'mp4';
        formData.append('file', audioBlob, `recording.${ext}`);

        const res = await axios.post(`${API}/ai/hybrid/transcribe`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setIsTranscribing(false);
        return res.data;
      } catch (error) {
        setIsTranscribing(false);
        const detail = error.response?.data?.detail || 'Cloud transcription failed';
        return { success: false, error: detail };
      }
    } else {
      // Local: send to Web Worker
      if (localModelFailed) {
        setIsTranscribing(false);
        // Auto-fallback to backend transcription
        try {
          const formData = new FormData();
          const ext = audioBlob.type.includes('webm') ? 'webm' : 'mp4';
          formData.append('file', audioBlob, `recording.${ext}`);
          const res = await axios.post(`${API}/transcribe`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          return res.data;
        } catch (err) {
          return { success: false, error: 'Transcription unavailable' };
        }
      }

      if (!localModelReady) {
        if (!workerRef.current) {
          initLocalWhisper();
        }
        setIsTranscribing(false);
        return { success: false, error: 'Local model still loading. Please wait and try again.' };
      }

      // Convert blob to Float32Array for the worker
      try {
        const arrayBuffer = await audioBlob.arrayBuffer();
        const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        const float32 = audioBuffer.getChannelData(0); // Mono

        return new Promise((resolve) => {
          pendingResolveRef.current = resolve;
          workerRef.current.postMessage({
            type: 'transcribe',
            audio: float32,
          });
        });
      } catch (error) {
        setIsTranscribing(false);
        return { success: false, error: `Audio processing error: ${error.message}` };
      }
    }
  }, [mode, localModelReady, localModelFailed, initLocalWhisper]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
      }
    };
  }, []);

  const value = {
    mode,
    setMode,
    tier,
    localModelReady,
    localModelLoading,
    localModelProgress,
    localModelFailed,
    isTranscribing,
    initLocalWhisper,
    transcribe,
    fetchTier,
  };

  return (
    <TranscriptionContext.Provider value={value}>
      {children}
    </TranscriptionContext.Provider>
  );
};

export const useTranscription = () => {
  const ctx = useContext(TranscriptionContext);
  if (!ctx) throw new Error('useTranscription must be used within TranscriptionProvider');
  return ctx;
};
